import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, Activity } from "lucide-react";

export default function EventMode() {
  const { eventId } = useParams();
  const [stats, setStats] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/live-stats`);
      setStats(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  if (!stats)
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500 bg-black">
        Loading...
      </div>
    );

  const pct = Math.min(
    100,
    Math.round((stats.checked_in / Math.max(1, stats.registered)) * 100)
  );

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <div className="p-4 flex items-center justify-between border-b border-zinc-800">
        <Link
          to={`/organizer/events/${eventId}`}
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="back-to-event-detail"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
          Live · refresh 4s
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-center p-6 md:p-16">
        <p className="overline text-zinc-500 mb-4">Event Mode</p>
        <h1
          className="font-heading text-4xl md:text-6xl font-semibold tracking-tighter mb-16"
          data-testid="live-event-name"
        >
          {stats.event.name}
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800">
          <Stat
            label="Registered"
            value={stats.registered}
            suffix={`/ ${stats.event.capacity}`}
            testid="live-registered"
          />
          <Stat
            label="Checked-in"
            value={stats.checked_in}
            accent
            testid="live-checkedin"
          />
          <Stat
            label="Remaining seats"
            value={stats.remaining}
            testid="live-remaining"
          />
        </div>

        <div className="mt-12">
          <div className="flex items-center justify-between text-sm text-zinc-400 mb-3">
            <span>Entry progress</span>
            <span>{pct}% of registrations checked in</span>
          </div>
          <div className="w-full bg-zinc-900 h-3 rounded-sm overflow-hidden border border-zinc-800">
            <div
              className="h-full bg-green-500 transition-all"
              style={{ width: `${pct}%` }}
              data-testid="live-progress-bar"
            />
          </div>
        </div>

        <div className="mt-12 flex gap-3 flex-wrap">
          <Link to={`/organizer/events/${eventId}/scan`}>
            <Button
              className="bg-white text-black hover:bg-zinc-200 rounded-md h-11 px-6"
              data-testid="event-mode-scan-button"
            >
              <Activity className="w-4 h-4 mr-2" />
              Open scanner
            </Button>
          </Link>
        </div>
      </div>

      <div className="p-6 text-xs text-zinc-600 border-t border-zinc-800">
        Pending check-in: {stats.pending_checkin} · Pending payment:{" "}
        {stats.pending_payment}
      </div>
    </div>
  );
}

function Stat({ label, value, suffix, accent, testid }) {
  return (
    <div className="bg-black p-8 md:p-10">
      <p className="overline text-zinc-500 mb-4">{label}</p>
      <div className="flex items-baseline gap-2">
        <span
          className={`font-heading text-6xl md:text-8xl font-semibold tracking-tighter ${
            accent ? "text-green-400" : ""
          }`}
          data-testid={testid}
        >
          {value}
        </span>
        {suffix && <span className="text-zinc-500 text-xl">{suffix}</span>}
      </div>
    </div>
  );
}
